import { GetStorageHandlerPayload } from '@hqo/hqo-miniapp-client-sdk';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useMiniappSdk, useMiniappSdkEventHandler } from 'components/miniapp-sdk-provider/miniapp-sdk.hook';
import { hydrateBookingsTab } from 'store/bookings-tab/actions';
import { BOOKINGS_TAB_HOST_ANSWER_TIMEOUT_MS, BOOKINGS_TAB_STORAGE_KEY } from 'store/bookings-tab/consts';
import { readStoredBookingsTab } from 'store/bookings-tab/utils';
import { useHostInfo } from 'store/host-info/hooks/use-host-info';

/**
 * Brings the host's stored bookings tab into the store, once, on boot.
 *
 * Mounted in `app`, and only there -- NOT in the bookings page, which is where the tab is actually
 * read. `sdk.on` has no counterpart: the SDK pushes handlers onto an array and offers no way to
 * remove one, and `useMiniappSdkEventHandler` registers without a cleanup. So a listener in a
 * component that remounts accumulates a copy per mount, every one of which fires -- and the
 * bookings page mounts again every time the tenant comes back to it. Registered once with stable
 * dependencies, in something that mounts once, that cannot happen.
 *
 * Unlike the booking selection this waits only for the CLIENT. The entry is not scoped to a
 * building, so there is nothing here that the building fetch has to land first for.
 *
 * ⛔ ASKED ONLY IN THE TENANT PORTAL, because that is the only host the entry is written for (see
 * `useBookingsTab`). Elsewhere this is a question about a key nothing wrote -- and a question nobody
 * answers is the one thing the page cannot sit through, so the wait is ended directly instead.
 */
export const useSyncBookingsTabWithHostApp = (): void => {
  const client = useMiniappSdk();
  const dispatch = useDispatch();
  const { isTenantPortalHost, isHostInfoSettled } = useHostInfo();

  useMiniappSdkEventHandler(
    'getStorageResponse',
    (data: GetStorageHandlerPayload) => {
      // One listener per key the app reads, all of them hearing every answer; anything else is
      // somebody else's.
      if (!data || !(BOOKINGS_TAB_STORAGE_KEY in data)) return;

      // Typed as a string, but the host hands back `localStorage.getItem`, which is null for a key
      // that was never written.
      const raw = data[BOOKINGS_TAB_STORAGE_KEY] as string | null;

      /*
       * ⚠️ HYDRATION, NOT A CHOICE. This answer is a postMessage round trip behind the question,
       * and the tenant can press a tab inside that window. The reducer drops it if they have --
       * see `hasLocalChoice`.
       */
      dispatch(hydrateBookingsTab(readStoredBookingsTab(raw)));
    },
    [],
  );

  const hasAskedRef = useRef(false);

  useEffect(() => {
    /*
     * Asked once per load. The entry answers "which tab was open when this frame was last built",
     * a question with one moment -- re-asking later would hand back a tab this app has since moved
     * past and switch to it under the tenant.
     */
    if (!isTenantPortalHost || !client || hasAskedRef.current) return;

    hasAskedRef.current = true;
    client.storage.get(BOOKINGS_TAB_STORAGE_KEY);
  }, [isTenantPortalHost, client]);

  /*
   * ⭐ THE WAIT ENDS THE MOMENT THE HOST NAMES ITSELF AS SOMETHING ELSE. Nothing is coming -- the
   * question above was never asked -- and the page holds its automatic first-booking pick until the
   * wait is over. Without this the only thing that ever ended it on these hosts was the floor below,
   * so every non-portal load paid the full deadline for an answer that was never in flight.
   *
   * `null` rather than a tab: this is the absence of an answer, and the reducer's empty-payload arm
   * ends the wait while leaving the tab exactly where it is.
   */
  useEffect(() => {
    if (!isHostInfoSettled || isTenantPortalHost) return;

    dispatch(hydrateBookingsTab(null));
  }, [isHostInfoSettled, isTenantPortalHost, dispatch]);

  useEffect(() => {
    /*
     * ⚠️ THE ARM FOR A HOST THAT NEVER ANSWERS. `getStorageResponse` is the only thing that ends the
     * wait, and nothing guarantees it arrives -- an older shell with no handler for this key simply
     * says nothing back, forever. The page holds its automatic first-booking pick until the wait is
     * over (see `isHostAnswerPending`), so a wait with no floor is a pane layout that never opens
     * anything at all.
     *
     * ⭐ ARMED AT MOUNT, AND ON EVERY HOST, which is what makes it a floor rather than one more thing
     * waiting on an answer. `isSettled` has no deadline of its own -- only `getHostInfoResponse` ever
     * sets it -- so a shell that answers neither question would leave both of the effects above
     * holding and the wait raised for the life of the page. Armed here it cannot: this one depends on
     * nothing the host has to say.
     *
     * `null` rather than a tab: this is the absence of an answer, and the reducer's empty-payload arm
     * ends the wait while leaving the tab exactly where it is.
     *
     * ⭐ AND THIS IS THE ONLY THING THAT MINTS A SECOND ANSWER. The question goes out once per load,
     * so the host's real answer arriving after this has fired is an answer to a question already
     * settled -- and by then the pane has taken the ended wait as leave to pick a booking out of the
     * tab on screen. `hasLocalChoice` does NOT catch it: nobody pressed anything, which is exactly
     * why the pick happened. The reducer drops a hydration landing on a settled slice for that
     * reason; this floor sits well clear of a normal round trip so it rarely comes to that.
     */
    const timer = window.setTimeout(() => dispatch(hydrateBookingsTab(null)), BOOKINGS_TAB_HOST_ANSWER_TIMEOUT_MS);

    return () => window.clearTimeout(timer);
  }, [dispatch]);
};
